/**
 * ClientFilterDropdown — BLU-3xx Phase 47.01.2-B-03
 *
 * Per-client filter for the Inventory table at shared 3PL locations. Mirrors
 * the BLU-389 OrdersPage filter shape: "All clients" sentinel + caller's own
 * org first + connected client orgs that are actually present in the data.
 *
 * Two render modes:
 *  - <=6 distinct clients (default): shadcn Select dropdown — fast, compact.
 *  - >6 distinct clients: cmdk Combobox in a Popover — typeahead inside the
 *    dropdown. Threshold is 6 not 10 because Selects with 7+ items already
 *    feel cramped (frontend review note in plan).
 *
 * Visibility is controlled by the parent — if `useClientOrgFilter` returns
 * `showClientFilter: false`, do not render this component at all.
 */
import { useMemo, useState } from 'react';
import { Filter, Check, ChevronsUpDown, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

import { ALL_CLIENTS } from '../hooks/useClientOrgFilter';
import { hexSlice } from '../utils/partnerFallback';

// Selects with more than this many options feel cramped. Switch to a
// Combobox with typeahead instead. Per frontend review: lowered from 10 to 6.
const TYPEAHEAD_THRESHOLD = 6;

export interface ClientFilterDropdownProps {
  value: string;
  onChange: (next: string) => void;
  ownOrgId?: string;
  /** Resolved org id → display name. Falls back to "Partner #N" when missing. */
  orgIdToName: Map<string, string>;
  /** Org ids actually present in the data (drives the option list). */
  distinctOrgsInData: string[];
  /** Optional aria-label override. */
  ariaLabel?: string;
}

export function ClientFilterDropdown({
  value,
  onChange,
  ownOrgId,
  orgIdToName,
  distinctOrgsInData,
  ariaLabel,
}: ClientFilterDropdownProps) {
  const { t } = useTranslation('inventory');
  const resolvedAriaLabel = ariaLabel ?? t('crossOrg.filter.ariaLabel');
  // Sort options: own org first (when present in data), then connected
  // clients alphabetically by display name. Stable across renders so the
  // dropdown order doesn't jitter while data is fetching.
  const sortedOrgIds = useMemo(() => {
    const result: string[] = [];
    if (ownOrgId && distinctOrgsInData.includes(ownOrgId)) {
      result.push(ownOrgId);
    }
    const others = distinctOrgsInData
      .filter((id) => id !== ownOrgId)
      .sort((a, b) => {
        const aName = orgIdToName.get(a) ?? a;
        const bName = orgIdToName.get(b) ?? b;
        return aName.localeCompare(bName);
      });
    result.push(...others);
    return result;
  }, [distinctOrgsInData, ownOrgId, orgIdToName]);

  // BLU-3xx Phase 47.01.2-B-fix2 NEW-FH2: hex slice (not 1-based index)
  // so the same unresolved org carries the same label across surfaces
  // (ClientFilterDropdown / ReceiveHistoryTable / ReverseReceiptDialog).
  // The previously-passed `fallbackIndex` is no longer used; signature
  // kept for call-site compatibility but ignored.
  const labelFor = (orgId: string, _fallbackIndex: number) =>
    orgIdToName.get(orgId) ?? t('crossOrg.partnerFallbackHex', { hex: hexSlice(orgId) });

  const useTypeahead = sortedOrgIds.length > TYPEAHEAD_THRESHOLD;

  if (useTypeahead) {
    return (
      <ClientFilterCombobox
        value={value}
        onChange={onChange}
        sortedOrgIds={sortedOrgIds}
        labelFor={labelFor}
        ariaLabel={resolvedAriaLabel}
        totalCount={distinctOrgsInData.length}
      />
    );
  }

  return (
    <div className="flex items-center gap-1.5">
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="w-[220px] gap-2 h-8" aria-label={resolvedAriaLabel}>
          <Filter
            className="h-3.5 w-3.5 text-muted-foreground shrink-0"
            aria-hidden="true"
          />
          <SelectValue placeholder={t('crossOrg.filter.allClients')} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL_CLIENTS}>
            {t('crossOrg.filter.allClientsCount', { count: distinctOrgsInData.length })}
          </SelectItem>
          {sortedOrgIds.map((id, idx) => (
            <SelectItem key={id} value={id}>
              {labelFor(id, idx)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {value !== ALL_CLIENTS && (
        <Button
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
          onClick={() => onChange(ALL_CLIENTS)}
          aria-label={t('crossOrg.filter.clear')}
        >
          <X className="h-3.5 w-3.5" aria-hidden="true" />
        </Button>
      )}
    </div>
  );
}

interface ComboboxProps {
  value: string;
  onChange: (next: string) => void;
  sortedOrgIds: string[];
  labelFor: (orgId: string, idx: number) => string;
  ariaLabel: string;
  totalCount: number;
}

function ClientFilterCombobox({
  value,
  onChange,
  sortedOrgIds,
  labelFor,
  ariaLabel,
  totalCount,
}: ComboboxProps) {
  const { t } = useTranslation('inventory');
  const [open, setOpen] = useState(false);

  const triggerLabel =
    value === ALL_CLIENTS
      ? t('crossOrg.filter.allClientsCount', { count: totalCount })
      : (() => {
          const idx = sortedOrgIds.indexOf(value);
          return labelFor(value, idx === -1 ? 0 : idx);
        })();

  return (
    <div className="flex items-center gap-1.5">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            aria-label={ariaLabel}
            className="w-[240px] justify-between h-8 gap-2 font-normal"
          >
            <span className="flex items-center gap-2 min-w-0">
              <Filter
                className="h-3.5 w-3.5 text-muted-foreground shrink-0"
                aria-hidden="true"
              />
              <span className="truncate">{triggerLabel}</span>
            </span>
            <ChevronsUpDown
              className="h-3.5 w-3.5 text-muted-foreground shrink-0 opacity-60"
              aria-hidden="true"
            />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[280px] p-0" align="start">
          <Command>
            <CommandInput placeholder={t('crossOrg.filter.searchPlaceholder')} />
            <CommandList>
              <CommandEmpty>{t('crossOrg.filter.noMatches')}</CommandEmpty>
              <CommandGroup>
                <CommandItem
                  value="__all__-all-clients"
                  onSelect={() => {
                    onChange(ALL_CLIENTS);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      value === ALL_CLIENTS ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                  {t('crossOrg.filter.allClientsCount', { count: totalCount })}
                </CommandItem>
              </CommandGroup>
              <CommandSeparator />
              <CommandGroup>
                {sortedOrgIds.map((id, idx) => {
                  const label = labelFor(id, idx);
                  return (
                    <CommandItem
                      key={id}
                      value={`${label} ${id}`}
                      onSelect={() => {
                        onChange(id);
                        setOpen(false);
                      }}
                    >
                      <Check
                        className={cn(
                          'mr-2 h-4 w-4',
                          value === id ? 'opacity-100' : 'opacity-0',
                        )}
                      />
                      <span className="truncate">{label}</span>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {value !== ALL_CLIENTS && (
        <Button
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
          onClick={() => onChange(ALL_CLIENTS)}
          aria-label={t('crossOrg.filter.clear')}
        >
          <X className="h-3.5 w-3.5" aria-hidden="true" />
        </Button>
      )}
    </div>
  );
}
